/**
 * src/utils/countryFlagPalette.ts
 *
 * The stripe colours of a country's flag, read off the flag itself.
 *
 * Painting a flag onto a building needs ordered band colours, not an
 * image. The alternative was a hand-maintained table of roughly two
 * hundred palettes, which would be wrong somewhere on the day it was
 * written and wrong somewhere else a year later. flagcdn.com already
 * serves the flags this app shows as badges, and it sends
 * `access-control-allow-origin: *`, so the bitmap can be read back off a
 * canvas and the colours taken from the source of truth.
 *
 * Bands are found by the *modal* colour of each row and column rather
 * than a single centre pixel, so an emblem in the middle of a flag — the
 * disc on Japan's, the crest on Mexico's — does not get mistaken for a
 * stripe. The axis with more bands wins, which is what distinguishes
 * horizontal stripes from vertical ones.
 *
 * What comes out is a `FlagReading` — stripes, orientation, hoist bar,
 * canton, and colours ranked by coverage — not a finished answer. Turning
 * one into bands up a tower is `flagArrangements`' job, because the same
 * flag has several defensible arrangements and the choice belongs to
 * whoever can see the building.
 *
 * Extraction failure returns an empty list and the caller skips flag
 * rendering. Inventing plausible colours for a national flag would be
 * worse than not drawing one.
 */

import {
  defaultArrangement,
  type FlagReading,
} from './flagArrangements';

const FLAG_IMAGE_BASE = 'https://flagcdn.com/w80';
/**
 * Bumped when the extraction changes shape, because a cached palette is
 * indistinguishable from a fresh one: the UAE would otherwise keep the
 * three-colour reading that dropped its hoist, and v2 stored bands where
 * v3 stores the whole reading.
 */
const CACHE_KEY = 'geotrust:flag-reading-v3';
const CACHE_MAX = 120;

/** Two rows are the same band while they stay this close in RGB. */
const BAND_MERGE_DISTANCE = 44;

/** Runs thinner than this share of the axis are edging, not a stripe. */
const MIN_BAND_SHARE = 0.06;

/** Flags are opaque; a transparent pixel is padding. */
const MIN_ALPHA = 128;

/**
 * How much of a line one colour must cover for that line to count as part
 * of a hoist band. High on purpose: a hoist runs the full length of its
 * edge, which is what separates the UAE's red bar from Japan's disc or an
 * American canton.
 */
const HOIST_PURITY = 0.85;

/** A hoist narrower or wider than this share of the axis is not one. */
const HOIST_MIN_SHARE = 0.08;
const HOIST_MAX_SHARE = 0.45;

/**
 * How much of a line inside a canton must be the canton's own colour.
 *
 * Lower than `HOIST_PURITY` on purpose: a canton is allowed to be
 * decorated, and the American union is nearly half stars. Requiring the
 * purity of a plain bar would reject the one flag this exists for.
 */
const CANTON_SHARE = 0.5;

/**
 * A canton stops short of both edges. Reaching the far edge makes it a
 * stripe; reaching the bottom makes it a hoist bar, which is read
 * separately and does not want reading twice.
 */
const CANTON_MIN_WIDTH = 0.15;
const CANTON_MAX_WIDTH = 0.62;
const CANTON_MIN_HEIGHT = 0.2;
const CANTON_MAX_HEIGHT = 0.72;

/** A colour covering less of the flag than this is trim, not a colour of it. */
const DOMINANT_MIN_SHARE = 0.03;

/**
 * How close to the line between two other colours makes a colour a blend
 * of them. Tighter than `BAND_MERGE_DISTANCE`, which asks a different
 * question — whether two colours are the same — and would swallow real
 * ones here.
 */
const BLEND_DISTANCE = 24;

type CacheEntry = { reading: FlagReading; at: number };

const memory = new Map<string, FlagReading>();
const inFlight = new Map<string, Promise<FlagReading | null>>();

// ── Content refs ─────────────────────────────────────────────────────────────

/**
 * The approved-content id for a country flag.
 *
 * Carrying the country in the ref keeps the allow-list meaningful: an
 * operator has approved *their* flag, not "whatever flag the caller asks
 * for", which is the whole point of the list.
 */
export function flagContentRef(iso2: string): string {
  return `country-flag:${String(iso2 || '').trim().toUpperCase()}`;
}

/** ISO-2 out of a `country-flag:XX` ref, or null if it is not one. */
export function parseFlagContentRef(ref: unknown): string | null {
  if (typeof ref !== 'string') return null;
  const m = ref.trim().match(/^country-flag:([A-Za-z]{2})$/);
  return m ? m[1].toUpperCase() : null;
}

// ── Pure banding ─────────────────────────────────────────────────────────────

function toHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function distance(a: [number, number, number], b: [number, number, number]): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);
}

/**
 * The dominant colour of one line of pixels.
 *
 * Pixels are bucketed coarsely to find the majority colour, then the
 * average of just that bucket is returned — the bucket finds the stripe,
 * the average keeps the actual shade rather than a quantised one.
 */
function modalColour(
  pixels: ArrayLike<number>,
  indices: number[],
): [number, number, number] | null {
  type Bucket = { n: number; r: number; g: number; b: number };
  const buckets = new Map<number, Bucket>();

  for (const i of indices) {
    const a = pixels[i + 3];
    if (a != null && a < MIN_ALPHA) continue;
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const cur = buckets.get(key);
    if (cur) {
      cur.n += 1; cur.r += r; cur.g += g; cur.b += b;
    } else {
      buckets.set(key, { n: 1, r, g, b });
    }
  }

  const values = Array.from(buckets.values());
  let best: Bucket | null = null;
  for (let i = 0; i < values.length; i += 1) {
    if (!best || values[i].n > best.n) best = values[i];
  }
  if (!best) return null;
  return [best.r / best.n, best.g / best.n, best.b / best.n];
}

/** Collapse a sequence of line colours into contiguous bands. */
function runsOf(
  line: Array<[number, number, number] | null>,
): Array<{ colour: [number, number, number]; length: number }> {
  const runs: Array<{ colour: [number, number, number]; length: number; n: number }> = [];

  for (const c of line) {
    if (!c) continue;
    const last = runs[runs.length - 1];
    if (last && distance(last.colour, c) <= BAND_MERGE_DISTANCE) {
      // Rolling average keeps a gradient from dragging the band away
      // from the colour most of its rows actually are.
      last.colour = [
        (last.colour[0] * last.n + c[0]) / (last.n + 1),
        (last.colour[1] * last.n + c[1]) / (last.n + 1),
        (last.colour[2] * last.n + c[2]) / (last.n + 1),
      ];
      last.n += 1;
      last.length += 1;
    } else {
      runs.push({ colour: c, length: 1, n: 1 });
    }
  }

  return runs.map(({ colour, length }) => ({ colour, length }));
}

/**
 * Rejoin runs of the same colour that became neighbours.
 *
 * Dropping runs too thin to be stripes can leave two survivors of the
 * same colour side by side, with the sliver that separated them gone.
 * That is one stripe, not two: the Stars and Stripes came out as
 * `red, white, red, red, white, red` because a white run under the
 * minimum thickness was removed from between two reds and nothing put
 * them back together.
 */
function mergeAdjacent(
  runs: Array<{ colour: [number, number, number]; length: number }>,
): Array<{ colour: [number, number, number]; length: number }> {
  const out: Array<{ colour: [number, number, number]; length: number }> = [];
  for (const run of runs) {
    const last = out[out.length - 1];
    if (last && distance(last.colour, run.colour) <= BAND_MERGE_DISTANCE) {
      const total = last.length + run.length;
      last.colour = [
        (last.colour[0] * last.length + run.colour[0] * run.length) / total,
        (last.colour[1] * last.length + run.colour[1] * run.length) / total,
        (last.colour[2] * last.length + run.colour[2] * run.length) / total,
      ];
      last.length = total;
    } else {
      out.push({ colour: run.colour, length: run.length });
    }
  }
  return out;
}

/** The literal pixel colours along one line, transparent pixels dropped. */
function pixelLine(
  pixels: ArrayLike<number>,
  indices: number[],
): Array<[number, number, number] | null> {
  return indices.map((i) => {
    const a = pixels[i + 3];
    if (a != null && a < MIN_ALPHA) return null;
    return [pixels[i], pixels[i + 1], pixels[i + 2]] as [number, number, number];
  });
}

/**
 * How many bands a single scan line crosses.
 *
 * Orientation is decided from lines near the *edges*, because that is
 * where flags are least decorated. An emblem sits in the middle — the
 * disc on Japan's flag, the crest on Mexico's — so a centre scan reports
 * stripes that are not there; a canton sits in one corner, so a single
 * edge is not enough either and both are consulted. Colour is still taken
 * from whole-line modal readings, which is what keeps the emblem out of
 * the palette once the axis is known.
 */
function bandsAcross(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  axis: 'down' | 'across',
  at: number,
): number {
  const span = axis === 'down' ? height : width;
  const idx: number[] = [];
  for (let n = 0; n < span; n += 1) {
    idx.push(axis === 'down' ? (n * width + at) * 4 : (at * width + n) * 4);
  }
  return runsOf(pixelLine(pixels, idx)).filter((r) => r.length >= span * MIN_BAND_SHARE).length;
}

/** How far in from an edge to scan; clear of any one-pixel border. */
function inset(span: number): number {
  return Math.max(0, Math.min(span - 1, Math.round(span * 0.06)));
}

/** A line's dominant colour together with how much of the line it covers. */
function linePurity(
  pixels: ArrayLike<number>,
  indices: number[],
): { colour: [number, number, number]; purity: number } | null {
  const modal = modalColour(pixels, indices);
  if (!modal) return null;

  let counted = 0;
  let matching = 0;
  for (const i of indices) {
    const a = pixels[i + 3];
    if (a != null && a < MIN_ALPHA) continue;
    counted += 1;
    const here: [number, number, number] = [pixels[i], pixels[i + 1], pixels[i + 2]];
    if (distance(modal, here) <= BAND_MERGE_DISTANCE) matching += 1;
  }
  if (counted === 0) return null;
  return { colour: modal, purity: matching / counted };
}

/**
 * The colour of a hoist band, if the flag has one.
 *
 * A hoist is a bar at one edge running the *full length* of that edge,
 * across the stripes rather than with them — the red bar on the UAE's
 * flag, the black one on Kuwait's. Choosing a single stripe axis throws
 * it away, which is why a UAE building came out green, white and black
 * with no red anywhere.
 *
 * Every test here is there to reject a near-miss: full-length coverage
 * rejects an American canton, which stops two thirds of the way down;
 * the purity floor rejects Japan's disc, which is not a bar at all; and
 * the distinctness check rejects the plain field of a single-colour flag,
 * whose edge is genuinely uniform but says nothing new.
 *
 * @param axis `'down'` when the stripes are horizontal, so the hoist is
 *   sought along the columns; `'across'` for the opposite.
 */
function hoistColour(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  axis: 'down' | 'across',
  stripes: Array<[number, number, number]>,
): [number, number, number] | null {
  const lineCount = axis === 'down' ? width : height;
  const lineSpan = axis === 'down' ? height : width;
  if (lineCount < 4) return null;

  const lines = Array.from({ length: lineCount }, (_, n) => {
    const idx: number[] = [];
    for (let m = 0; m < lineSpan; m += 1) {
      idx.push(axis === 'down' ? (m * width + n) * 4 : (n * width + m) * 4);
    }
    return linePurity(pixels, idx);
  });

  /** Length of the uniform run starting at one end. */
  const runFrom = (start: number, step: number): { colour: [number, number, number]; length: number } | null => {
    const head = lines[start];
    if (!head || head.purity < HOIST_PURITY) return null;
    let length = 0;
    for (let n = start; n >= 0 && n < lineCount; n += step) {
      const line = lines[n];
      if (!line || line.purity < HOIST_PURITY) break;
      if (distance(line.colour, head.colour) > BAND_MERGE_DISTANCE) break;
      length += 1;
    }
    return { colour: head.colour, length };
  };

  const candidates = [runFrom(0, 1), runFrom(lineCount - 1, -1)]
    .filter((r): r is { colour: [number, number, number]; length: number } => r != null)
    .filter((r) => r.length >= lineCount * HOIST_MIN_SHARE && r.length <= lineCount * HOIST_MAX_SHARE)
    // A colour already read as a stripe is the same band seen twice.
    .filter((r) => stripes.every((s) => distance(s, r.colour) > BAND_MERGE_DISTANCE))
    .sort((a, b) => b.length - a.length);

  return candidates.length > 0 ? candidates[0].colour : null;
}

/** Share of the pixels at these indices that sit near `colour`. */
function shareNear(
  pixels: ArrayLike<number>,
  indices: number[],
  colour: [number, number, number],
): number {
  let counted = 0;
  let matching = 0;
  for (const i of indices) {
    const a = pixels[i + 3];
    if (a != null && a < MIN_ALPHA) continue;
    counted += 1;
    if (distance(colour, [pixels[i], pixels[i + 1], pixels[i + 2]]) <= BAND_MERGE_DISTANCE) {
      matching += 1;
    }
  }
  return counted === 0 ? 0 : matching / counted;
}

/**
 * The colour of a canton, if the flag has one.
 *
 * A canton is a block in the upper hoist corner that stops short of both
 * the far edge and the bottom — the American union, sitting beside seven
 * of its thirteen stripes rather than above any of them. `hoistColour`
 * deliberately rejects it, because a canton is not a bar, and the result
 * was a US building painted red and white with no blue anywhere.
 *
 * The block is measured rather than assumed: the corner's colour is taken
 * from a small probe, then followed right and down for as long as it keeps
 * the majority. Following it is what tells a canton from a hoist bar,
 * which never stops going down, and from a top stripe, which never stops
 * going right.
 */
function cantonColour(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
  stripes: Array<[number, number, number]>,
  hoist: [number, number, number] | null,
): [number, number, number] | null {
  if (width < 8 || height < 8) return null;

  const x0 = inset(width);
  const y0 = inset(height);
  const probeW = Math.max(1, Math.round(width * 0.1));
  const probeH = Math.max(1, Math.round(height * 0.1));

  const probe: number[] = [];
  for (let y = y0; y < Math.min(height, y0 + probeH); y += 1) {
    for (let x = x0; x < Math.min(width, x0 + probeW); x += 1) probe.push((y * width + x) * 4);
  }
  const colour = modalColour(pixels, probe);
  if (!colour) return null;

  // A colour already read as a stripe is that stripe seen in the corner,
  // and a colour already read as the hoist is the bar read twice.
  if (stripes.some((s) => distance(s, colour) <= BAND_MERGE_DISTANCE)) return null;
  if (hoist && distance(hoist, colour) <= BAND_MERGE_DISTANCE) return null;

  // Width first, across the upper part only, so the stripes below cannot
  // vote on how far the canton reaches.
  const upper = Math.max(1, Math.floor(height * 0.4));
  let spanX = 0;
  for (let x = 0; x < width; x += 1) {
    const column: number[] = [];
    for (let y = 0; y < upper; y += 1) column.push((y * width + x) * 4);
    if (shareNear(pixels, column, colour) < CANTON_SHARE) break;
    spanX = x + 1;
  }
  if (spanX < width * CANTON_MIN_WIDTH || spanX > width * CANTON_MAX_WIDTH) return null;

  let spanY = 0;
  for (let y = 0; y < height; y += 1) {
    const row: number[] = [];
    for (let x = 0; x < spanX; x += 1) row.push((y * width + x) * 4);
    if (shareNear(pixels, row, colour) < CANTON_SHARE) break;
    spanY = y + 1;
  }
  if (spanY < height * CANTON_MIN_HEIGHT || spanY > height * CANTON_MAX_HEIGHT) return null;

  return colour;
}

/**
 * The flag's colours ranked by how much of it they cover.
 *
 * The last resort for a flag that is not striped in any direction: the
 * Union Jack read as rows gives blue, red, blue, which is a description
 * of a scan line and not of the flag. Coverage says blue, white, red,
 * which is at least true about the thing being painted.
 */
function dominantColours(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
): Array<[number, number, number]> {
  type Bucket = { n: number; r: number; g: number; b: number };
  const buckets = new Map<number, Bucket>();
  let total = 0;

  for (let i = 0; i < width * height * 4; i += 4) {
    const a = pixels[i + 3];
    if (a != null && a < MIN_ALPHA) continue;
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    total += 1;
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const cur = buckets.get(key);
    if (cur) {
      cur.n += 1; cur.r += r; cur.g += g; cur.b += b;
    } else {
      buckets.set(key, { n: 1, r, g, b });
    }
  }
  if (total === 0) return [];

  const coarse = Array.from(buckets.values())
    .map((v) => ({
      n: v.n,
      colour: [v.r / v.n, v.g / v.n, v.b / v.n] as [number, number, number],
    }))
    .sort((a, b) => b.n - a.n);

  // Quantisation splits one flat colour across neighbouring buckets and
  // anti-aliasing invents shades between two, so buckets are folded into
  // the largest colour they are close to before anything is ranked.
  const merged: Array<{ n: number; colour: [number, number, number] }> = [];
  for (const bucket of coarse) {
    const near = merged.find((m) => distance(m.colour, bucket.colour) <= BAND_MERGE_DISTANCE);
    if (near) near.n += bucket.n;
    else merged.push({ n: bucket.n, colour: bucket.colour });
  }

  const ranked = merged
    .filter((m) => m.n / total >= DOMINANT_MIN_SHARE)
    .sort((a, b) => b.n - a.n)
    .map((m) => m.colour);

  // A downscaled flag has a blended row at every boundary, and there are
  // enough boundaries that the blends clear any sane share threshold: the
  // US came out with three pinks between its red and its white. A blend
  // sits on the line between the two colours it came from, and those two
  // always cover more than it does, so anything lying on a line between
  // two already-accepted colours is an edge rather than a colour of the
  // flag. Raising the threshold instead would have thrown away thin but
  // real stripes, which is a worse trade.
  const kept: Array<[number, number, number]> = [];
  for (const colour of ranked) {
    if (!isBlendOf(colour, kept)) kept.push(colour);
  }
  return kept;
}

/** Distance from a colour to the straight line between two others. */
function distanceToSegment(
  p: [number, number, number],
  a: [number, number, number],
  b: [number, number, number],
): number {
  const ab: [number, number, number] = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const len2 = ab[0] ** 2 + ab[1] ** 2 + ab[2] ** 2;
  if (len2 === 0) return distance(p, a);
  const t = Math.max(0, Math.min(1,
    ((p[0] - a[0]) * ab[0] + (p[1] - a[1]) * ab[1] + (p[2] - a[2]) * ab[2]) / len2));
  return distance(p, [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t]);
}

function isBlendOf(
  colour: [number, number, number],
  others: Array<[number, number, number]>,
): boolean {
  for (let i = 0; i < others.length; i += 1) {
    for (let j = i + 1; j < others.length; j += 1) {
      if (distanceToSegment(colour, others[i], others[j]) <= BLEND_DISTANCE) return true;
    }
  }
  return false;
}

/**
 * Everything readable from a decoded flag bitmap.
 *
 * Exported for its own sake: this is the part worth testing, and it needs
 * no canvas, no network and no browser. What to *do* with the reading is
 * `flagArrangements`' business, deliberately not decided here — the same
 * flag is worn several defensible ways and the operator picks.
 *
 * @param pixels RGBA bytes, row-major, `width * height * 4` long.
 */
export function readFlagFromPixels(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
): FlagReading {
  const nothing: FlagReading = {
    stripes: [], orientation: 'horizontal', hoist: null, canton: null, dominant: [],
  };
  if (!width || !height) return nothing;

  const rowColours: Array<[number, number, number] | null> = [];
  for (let y = 0; y < height; y += 1) {
    const idx: number[] = [];
    for (let x = 0; x < width; x += 1) idx.push((y * width + x) * 4);
    rowColours.push(modalColour(pixels, idx));
  }

  const colColours: Array<[number, number, number] | null> = [];
  for (let x = 0; x < width; x += 1) {
    const idx: number[] = [];
    for (let y = 0; y < height; y += 1) idx.push((y * width + x) * 4);
    colColours.push(modalColour(pixels, idx));
  }

  // Merged after filtering, not before: the filter is what creates false
  // neighbours by removing the sliver between two runs of one colour.
  const horizontal = mergeAdjacent(
    runsOf(rowColours).filter((r) => r.length >= height * MIN_BAND_SHARE),
  );
  const vertical = mergeAdjacent(
    runsOf(colColours).filter((r) => r.length >= width * MIN_BAND_SHARE),
  );

  // Stripes cross the axis whose edge scans see more of them. Ties go to
  // horizontal, which is the commoner arrangement.
  const downEvidence = Math.max(
    bandsAcross(pixels, width, height, 'down', inset(width)),
    bandsAcross(pixels, width, height, 'down', width - 1 - inset(width)),
  );
  const acrossEvidence = Math.max(
    bandsAcross(pixels, width, height, 'across', inset(height)),
    bandsAcross(pixels, width, height, 'across', height - 1 - inset(height)),
  );

  const preferVertical = acrossEvidence > downEvidence;
  const chosen = preferVertical
    ? (vertical.length > 0 ? vertical : horizontal)
    : (horizontal.length > 0 ? horizontal : vertical);

  const hex = (c: [number, number, number]) => toHex(c[0], c[1], c[2]);
  const dominant = dominantColours(pixels, width, height).map(hex);

  // Stripes run one way. A reading that finds three or more bands *both*
  // ways is not looking at stripes but at something crossing the flag: the
  // Union Jack's crosses, the Nordic ones, Brazil's diamond. Slicing those
  // into rows gives blue, red, blue — a description of one scan line that
  // would paint a tower in a pattern the flag does not have. A genuine
  // A/B/A tricolour such as Nigeria's is unaffected, because its rows are
  // uniform and report a single band.
  const crossing = horizontal.length >= 3 && vertical.length >= 3;
  if (crossing) {
    return {
      stripes: [],
      orientation: preferVertical ? 'vertical' : 'horizontal',
      hoist: null,
      canton: null,
      dominant,
    };
  }

  if (chosen.length === 0) {
    // A single flat colour is still an answer, just not a striped one.
    const whole = modalColour(
      pixels,
      Array.from({ length: width * height }, (_, i) => i * 4),
    );
    return {
      ...nothing,
      stripes: whole ? [hex(whole)] : [],
      dominant,
    };
  }

  const stripes = chosen.map((r) => r.colour);
  const hoist = hoistColour(
    pixels,
    width,
    height,
    preferVertical ? 'across' : 'down',
    stripes,
  );
  const canton = cantonColour(pixels, width, height, stripes, hoist);

  return {
    stripes: stripes.map(hex),
    orientation: preferVertical ? 'vertical' : 'horizontal',
    hoist: hoist ? hex(hoist) : null,
    canton: canton ? hex(canton) : null,
    dominant,
  };
}

/**
 * The band colours a flag gets when nobody has chosen an arrangement.
 *
 * Kept as its own export because it is the shape the render path wants and
 * the shape every existing caller already asked for.
 */
export function extractBandsFromPixels(
  pixels: ArrayLike<number>,
  width: number,
  height: number,
): string[] {
  return defaultArrangement(readFlagFromPixels(pixels, width, height))?.bands ?? [];
}

// ── Band placement ───────────────────────────────────────────────────────────

/**
 * Which band a slab of building falls in.
 *
 * Keyed on the slab's own height rather than its position in the tier
 * array: OSM part order follows whatever Overpass returned, so tier 0 is
 * not reliably the ground floor, and using the index would scramble the
 * flag on some towers and not others.
 *
 * The flag reads top-down and the building reads bottom-up, so the first
 * band lands on the highest slab.
 */
export function bandColourForSlab(
  bands: string[],
  baseMeters: number,
  topMeters: number,
  peakMeters: number,
): string | null {
  if (bands.length === 0) return null;
  if (bands.length === 1) return bands[0];

  // OSM part heights are frequently absent, and an unplaceable slab must
  // still be painted: returning nothing here let the layer fall through
  // to the object's base colour, which is why a flagged tower showed grey
  // patches among its bands. The ground band is the honest guess for a
  // slab whose position cannot be worked out.
  const ground = bands[bands.length - 1];
  const base = Number.isFinite(baseMeters) ? Math.max(0, baseMeters) : null;
  const top = Number.isFinite(topMeters) ? Math.max(0, topMeters) : null;
  const mid = base != null && top != null ? (base + top) / 2 : (top ?? base);
  if (mid == null || !(peakMeters > 0)) return ground;

  const fromTop = 1 - Math.max(0, Math.min(1, mid / peakMeters));
  const i = Math.floor(fromTop * bands.length);
  return bands[Math.max(0, Math.min(bands.length - 1, i))];
}

// ── Cache ────────────────────────────────────────────────────────────────────

function readStore(): Record<string, CacheEntry> {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(iso2: string, reading: FlagReading): void {
  try {
    const store = readStore();
    store[iso2] = { reading, at: Date.now() };
    const trimmed = Object.entries(store)
      .sort((a, b) => (b[1]?.at ?? 0) - (a[1]?.at ?? 0))
      .slice(0, CACHE_MAX);
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    /* a full or disabled store is not worth failing a render over */
  }
}

function normalizeIso(iso2: unknown): string | null {
  const s = String(iso2 || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(s) ? s : null;
}

/** Enough of a reading to be worth trusting from a store we do not control. */
function usableReading(value: unknown): FlagReading | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as Partial<FlagReading>;
  const strings = (v: unknown): string[] =>
    (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string' && !!s) : []);

  const stripes = strings(r.stripes);
  const dominant = strings(r.dominant);
  if (stripes.length === 0 && dominant.length === 0) return null;

  return {
    stripes,
    orientation: r.orientation === 'vertical' ? 'vertical' : 'horizontal',
    hoist: typeof r.hoist === 'string' && r.hoist ? r.hoist : null,
    canton: typeof r.canton === 'string' && r.canton ? r.canton : null,
    dominant,
  };
}

/**
 * What is already known about a country's flag, without touching the
 * network.
 *
 * The render path is synchronous, so it reads through this and paints
 * nothing on a miss; `warmFlagBands` is what turns a miss into a hit. The
 * whole reading is cached rather than one band list, because the editor
 * offers every arrangement and re-fetching the bitmap to change which one
 * is worn would be absurd.
 */
export function getCachedFlagReading(iso2: string): FlagReading | null {
  const key = normalizeIso(iso2);
  if (!key) return null;

  const hit = memory.get(key);
  if (hit) return hit;

  const stored = usableReading(readStore()[key]?.reading);
  if (stored) {
    memory.set(key, stored);
    return stored;
  }
  return null;
}

/** The default arrangement's bands for a country already read. */
export function getCachedFlagBands(iso2: string): string[] | null {
  const reading = getCachedFlagReading(iso2);
  if (!reading) return null;
  const bands = defaultArrangement(reading)?.bands ?? [];
  return bands.length > 0 ? bands : null;
}

async function decodeFlag(iso2: string): Promise<FlagReading | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;

  const bitmap = await new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    // Required for canvas readback; flagcdn answers with `*`.
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `${FLAG_IMAGE_BASE}/${iso2.toLowerCase()}.png`;
  });
  if (!bitmap) return null;

  const width = bitmap.naturalWidth || bitmap.width;
  const height = bitmap.naturalHeight || bitmap.height;
  if (!width || !height) return null;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  try {
    ctx.drawImage(bitmap, 0, 0);
    const data = ctx.getImageData(0, 0, width, height).data;
    return usableReading(readFlagFromPixels(data, width, height));
  } catch {
    // A tainted canvas means the CORS grant did not apply after all.
    return null;
  }
}

/** Load and cache one country's flag. Concurrent calls share one fetch. */
export function getFlagReading(iso2: string): Promise<FlagReading | null> {
  const key = normalizeIso(iso2);
  if (!key) return Promise.resolve(null);

  const cached = getCachedFlagReading(key);
  if (cached) return Promise.resolve(cached);

  const existing = inFlight.get(key);
  if (existing) return existing;

  const task = decodeFlag(key)
    .then((reading) => {
      if (reading) {
        memory.set(key, reading);
        writeStore(key, reading);
      }
      return reading;
    })
    .finally(() => { inFlight.delete(key); });

  inFlight.set(key, task);
  return task;
}

/** The default arrangement's bands, loading the flag if it is not known yet. */
export function getFlagBands(iso2: string): Promise<string[]> {
  return getFlagReading(iso2).then((reading) => (
    reading ? defaultArrangement(reading)?.bands ?? [] : []
  ));
}

/**
 * Prefetch several countries.
 *
 * Resolves to true when anything new landed, which is the map layer's cue
 * that a repaint would now show something it could not show before.
 */
export async function warmFlagBands(codes: readonly string[]): Promise<boolean> {
  const wanted = new Set<string>();
  codes.forEach((c) => {
    const key = normalizeIso(c);
    if (key && !getCachedFlagReading(key)) wanted.add(key);
  });
  if (wanted.size === 0) return false;

  const results = await Promise.all(Array.from(wanted).map((k) => getFlagReading(k)));
  return results.some((reading) => reading != null);
}
